// src/logic/builder-state.js

// Estado inicial (Triple equivale a L)
const INITIAL_STATE = {
  size: 'Triple',
  toppings: []
};

// Estado de la hamburguesa que se está personalizando en el Interactive Lab
let state = {
  size: 'Triple',
  toppings: []
};

// Callbacks registrados para cambios de estado
const callbacks = [];

// Mapeo de tamaños aceptados a sus nombres oficiales
const SIZE_MAP = {
  's': 'Sencilla',
  'm': 'Doble',
  'l': 'Triple',
  'sencilla': 'Sencilla',
  'doble': 'Doble',
  'triple': 'Triple'
};

// Precios base por tamaño de hamburguesa (en Córdobas C$)
const BASE_PRICES = {
  'Sencilla': 290,
  'Doble': 360,
  'Triple': 430,
  // Compatibilidad con letras
  'S': 290,
  'M': 360,
  'L': 430
};

// Mapeo de toppings para robustez (asocia nombres antiguos o variaciones a los oficiales)
const TOPPING_MAP = {
  'mozzarella': 'cheddar',
  'cheddar': 'cheddar',
  'queso cheddar': 'cheddar',
  'queso cheddar fundido': 'cheddar',
  'queso': 'cheddar',
  
  'pepperoni': 'tocino',
  'tocino': 'tocino',
  'tocino crujiente': 'tocino',
  'tocino ahumado al maple': 'tocino',
  'bacon': 'tocino',
  
  'champiñones': 'cebolla caramelizada',
  'champiñon': 'cebolla caramelizada',
  'cebolla caramelizada': 'cebolla caramelizada',
  'cebolla': 'cebolla caramelizada',
  
  'jamón serrano': 'pepinillos',
  'jamon serrano': 'pepinillos',
  'pepinillos': 'pepinillos',
  'pepinillo': 'pepinillos'
};

// Precios de ingredientes adicionales (en Córdobas C$)
const TOPPING_PRICES = {
  'cheddar': 40,
  'tocino': 60,
  'cebolla caramelizada': 30,
  'pepinillos': 25
};

// Notifica a todos los callbacks del cambio de estado
function notify() {
  callbacks.forEach(callback => {
    try {
      callback({
        size: state.size,
        toppings: [...state.toppings]
      });
    } catch (e) {
      console.error('Error en el callback del builder state:', e);
    }
  });
}

/**
 * Cambia el tamaño de la hamburguesa que se está personalizando.
 * @param {string} size - El tamaño ('Sencilla', 'Doble', 'Triple' o 'S', 'M', 'L').
 */
export function setSize(size) {
  if (typeof size !== 'string') return;
  const normalized = size.toLowerCase().trim();
  const mappedSize = SIZE_MAP[normalized];
  if (mappedSize) {
    state.size = mappedSize;
    notify();
  }
}

/**
 * Agrega o quita un ingrediente de la hamburguesa actual.
 * @param {string} topping - Nombre del ingrediente.
 * @returns {boolean} true si se agregó, false si se quitó.
 */
export function toggleTopping(topping) {
  if (typeof topping !== 'string') return false;
  const normalized = topping.toLowerCase().trim();
  const mappedTopping = TOPPING_MAP[normalized] || normalized;
  const index = state.toppings.indexOf(mappedTopping);
  let added = false;

  if (index === -1) {
    state.toppings.push(mappedTopping);
    added = true;
  } else {
    state.toppings.splice(index, 1);
    added = false;
  }

  notify();
  return added;
}

/**
 * Obtiene el estado actual de la hamburguesa que se está personalizando.
 * @returns {Object} El estado actual { size, toppings }.
 */
export function getState() {
  return {
    size: state.size,
    toppings: [...state.toppings]
  };
}

/**
 * Calcula el precio total de la hamburguesa personalizada basada en el tamaño e ingredientes seleccionados.
 * @returns {number} El precio total calculado en Córdobas.
 */
export function getBurgerPrice() {
  const basePrice = BASE_PRICES[state.size] || 0;
  const toppingsPrice = state.toppings.reduce((total, topping) => {
    const price = TOPPING_PRICES[topping] || 0;
    return total + price;
  }, 0);

  return basePrice + toppingsPrice;
}

// Mantener compatibilidad si se llama getPizzaPrice en otros archivos
export const getPizzaPrice = getBurgerPrice;

/**
 * Reinicia el estado de la hamburguesa personalizada al inicial.
 */
export function resetBuilder() {
  state = {
    size: INITIAL_STATE.size,
    toppings: [...INITIAL_STATE.toppings]
  };
  notify();
}

/**
 * Registra un callback que se ejecutará cada vez que el estado de la hamburguesa cambie.
 * @param {Function} callback - La función callback que recibirá el nuevo estado.
 */
export function onBuilderChange(callback) {
  if (typeof callback === 'function') {
    callbacks.push(callback);
    // Ejecutar inmediatamente para sincronizar la UI inicial
    callback({
      size: state.size,
      toppings: [...state.toppings]
    });
  }
}

